import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AgencyAccountInfo from "../../components/profile/AgencyAccountInfo";
import AgentTransferPoints from "../../components/profile/AgentTransferPoints";
// import AgentSettings from "../../components/profile/AgentSettings";
import { userService } from "../../services/api";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { switchRole } from "../../store/slices/authSlice";
import { ROLES } from "../../config/roles";

const AgencyAdminProfile = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Dual role = the same condition that shows the header's role switch
  // (Header.js canSwitchRoles). For these users the personal details are
  // OWNED by the agent profile — the admin card here would be a second,
  // diverging editor for the same person (Marin 2026-08-12).
  const roles = useSelector((state) => state.auth?.roles);
  const isDualRole =
    Array.isArray(roles) &&
    roles.includes(ROLES.AgencyAdmin) &&
    roles.includes(ROLES.Agent);

  // Reuses the existing switch mechanic (authSlice.switchRole — the same
  // action the header dispatches); /profile re-renders as AgentProfile.
  const handleGoToAgentProfile = () => {
    dispatch(switchRole(ROLES.Agent));
    navigate("/profile");
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [agencyData, setAgencyData] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [formData, setFormData] = useState({
    // agency fields
    name: "",
    website: "",
    agency_profile_picture: null,
    // admin fields
    admin_name: "",
    license_number: "",
    profile_picture: null, // admin profile picture
  });
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  // T056 — per-field save errors. "Agency name" is required: clearing it
  // used to produce NO error and NO toast, because handleSave dropped the
  // empty field from the payload (`if (formData.name)`), the request then
  // succeeded without it, and the response's unchanged name was written
  // back while edit mode was exited unconditionally. The user saw their
  // input silently reverted. Both halves are fixed: the empty value is
  // caught before the request, and a rejected save keeps edit mode.
  const [fieldErrors, setFieldErrors] = useState({});

  const buildAdminName = (admin) => {
    if (!admin) return "";
    if (admin.name) return admin.name;
    const tokens = [admin.first_name, admin.last_name].filter(Boolean);
    return tokens.join(" ").trim();
  };

  // Get active tab from URL or default to account-info for agency admins
  const activeTab = searchParams.get("tab") || "account-info";

  // Update URL when tab changes
  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  // Fetch agency admin profile data using only agent API
  useEffect(() => {
    const fetchAgencyProfile = async () => {
      setLoading(true);
      try {
        const agentRes = await userService.getAgentProfile();
        const agent = agentRes?.data || null;

        // Extract agency details from agent response
        const agency = agent?.agency_details || {};
        const adminName = buildAdminName(agent);

        setAgencyData(agency);
        setAdminData(agent ? { ...agent, name: adminName } : null);

        setFormData({
          name: agency?.name || "",
          website: agency?.website || "",
          agency_document: null,
          admin_name: adminName,
          license_number: agent?.license_number || "",
          document: null, // admin profile picture
        });
      } catch (error) {
        console.error("Failed to fetch agency profile:", error);
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchAgencyProfile();
  }, []);

  const handleAgencyInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Also update the agencyData state for real-time UI updates
    setAgencyData((prev) => (prev ? { ...prev, [field]: value } : null));
    // T056: a field's error clears as soon as the user edits it again.
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleAdminInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      ...(field === "name" ? { admin_name: value } : { [field]: value }),
    }));
    // Also update the adminData state for real-time UI updates
    setAdminData((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleAgencyDocumentChange = (file) => {
    setFormData((prev) => ({ ...prev, agency_profile_picture: file }));
  };

  const handleAdminDocumentChange = (file) => {
    setFormData((prev) => ({ ...prev, profile_picture: file }));
  };

  const handleSave = async () => {
    // T056: required fields are checked BEFORE the request. An empty
    // agency name must never be sent — the endpoint writes agency_name
    // through verbatim, so shipping "" would blank the agency's name
    // instead of being rejected.
    const agencyName = (formData.name || "").trim();
    if (!agencyName) {
      setFieldErrors((prev) => ({ ...prev, name: "Required." }));
      setError("");
      return; // stays in edit mode, keeps what the user typed
    }

    setIsSaving(true);
    setError("");
    setFieldErrors({});
    try {
      // Build single FormData payload for agent API with optional agency fields
      const formDataPayload = new FormData();

      // Agent fields. For dual-role users the admin card is not rendered
      // (the agent profile owns these fields), so this save must not touch
      // them either — nothing is sent, nothing can be nulled. Note the
      // fields were already append-if-truthy, so skipping them entirely is
      // the surgical version of the same behavior.
      if (!isDualRole) {
        if (formData.admin_name)
          formDataPayload.append("name", formData.admin_name);
        if (formData.license_number)
          formDataPayload.append("license_number", formData.license_number);
        if (formData.profile_picture instanceof File)
          formDataPayload.append("profile_picture", formData.profile_picture);
      }

      // Optional agency fields (only sent when caller is agency admin).
      // T056: the name is validated above, so it is always sent — it is
      // no longer silently dropped when the user cleared it.
      formDataPayload.append("agency_name", agencyName);
      if (formData.website)
        formDataPayload.append("agency_website", formData.website);
      if (formData.agency_profile_picture instanceof File)
        formDataPayload.append(
          "agency_profile_picture",
          formData.agency_profile_picture
        );

      const updateRes = await userService.updateAgentProfile(formDataPayload);
      // T056: userService.updateAgentProfile RESOLVES with
      // { success:false } on a rejected save instead of throwing, so the
      // catch below never ran. The old code then read `data` off that
      // result, wrote the resulting empty agency back over the card and
      // left edit mode — a rejection rendered as "nothing happened".
      if (!updateRes?.success) {
        setError("Failed to save changes");
        return; // stays in edit mode with the user's input intact
      }

      const updatedAgent = updateRes?.data || null;

      // Extract updated agency details from response
      const updatedAgency = updatedAgent?.agency_details || {};

      const updatedName = buildAdminName(updatedAgent);
      setAgencyData(updatedAgency);
      setAdminData(updatedAgent ? { ...updatedAgent, name: updatedName } : null);

      setIsEditing(false);
    } catch (e) {
      setError("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Revert form to loaded values
    setFormData({
      name: agencyData?.name || "",
      website: agencyData?.website || "",
      agency_document: null,
      admin_name: buildAdminName(adminData),
      license_number: adminData?.license_number || "",
      document: null,
    });
    setIsEditing(false);
    setError("");
    setFieldErrors({});
  };

  const tabs = [
    { id: "account-info", label: "Account Info" },
    { id: "my-transferpoints", label: "My TransferPoints" },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "account-info":
        return (
          <AgencyAccountInfo
            isEditing={isEditing}
            isLoading={loading}
            isSaving={isSaving}
            error={error}
            fieldErrors={fieldErrors}
            agencyData={agencyData}
            adminData={adminData}
            isDualRole={isDualRole}
            onGoToAgentProfile={handleGoToAgentProfile}
            onAgencyChange={handleAgencyInputChange}
            onAdminChange={handleAdminInputChange}
            onAgencyDocumentChange={handleAgencyDocumentChange}
            onAdminDocumentChange={handleAdminDocumentChange}
            onSave={handleSave}
          />
        );
      case "my-transferpoints":
        return <AgentTransferPoints />;
      default:
        return (
          <AgencyAccountInfo
            isEditing={isEditing}
            isLoading={loading}
            isSaving={isSaving}
            error={error}
            fieldErrors={fieldErrors}
            agencyData={agencyData}
            adminData={adminData}
            isDualRole={isDualRole}
            onGoToAgentProfile={handleGoToAgentProfile}
            onAgencyChange={handleAgencyInputChange}
            onAdminChange={handleAdminInputChange}
            onAgencyDocumentChange={handleAgencyDocumentChange}
            onAdminDocumentChange={handleAdminDocumentChange}
            onSave={handleSave}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <div className="mx-auto">
        {/* Header */}
        {isMobile ? (
          // Mobile Header Layout
          <div className="bg-white py-3 border-b border-[#E5E6E8]">
            <div className="px-4">
              <h1 className="text-base font-bold text-gray-900 mb-3">My Profile</h1>
              
              {activeTab === "account-info" && (
                <div className="flex flex-col gap-2">
                  <button 
                    onClick={() => navigate(`/search/agency/${agencyData?.agency_id}`)} 
                    className="bg-[#2283FF] w-full px-4 py-2 text-[#F5F5F5] rounded-lg text-sm font-medium"
                  >
                    Preview public profile
                  </button>

                  {!isEditing ? (
                    <button
                      className="w-full px-4 py-2 rounded-lg bg-[#2283FF] text-[#F5F5F5] text-sm font-medium"
                      onClick={() => setIsEditing(true)}
                      aria-label="Edit profile"
                    >
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        className="flex-1 border border-[#E6E6E6] bg-white text-[#1E1E1E] px-4 py-2 rounded-md font-medium transition-colors shadow-sm text-sm"
                        onClick={handleCancel}
                        disabled={isSaving}
                      >
                        Cancel
                      </button>
                      <button
                        className="flex-1 bg-[#2283FF] text-[#F5F5F5] px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-60 text-sm"
                        onClick={handleSave}
                        disabled={isSaving}
                      >
                        {isSaving ? "Saving..." : "Save"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          // Desktop Header Layout (unchanged)
          <div className="bg-white py-4 border-b border-[#E5E6E8]">
            <div className="sm:px-14 px-4 flex items-center justify-between">
              <h1 className="text-[20px] font-bold text-gray-900">My Profile</h1>
              
              {activeTab === "account-info" && (
                <div className="flex gap-3">
                  <button onClick={() => navigate(`/search/agency/${agencyData.agency_id}`)} className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg">
                    Preview public profile
                  </button>

                  {!isEditing ? (
                    <button
                      className="px-4 py-2 rounded-lg bg-[#2283FF] text-[#F5F5F5] text-sm font-medium"
                      onClick={() => setIsEditing(true)}
                      aria-label="Edit profile"
                    >
                      Edit
                    </button>
                  ) : (
                    <>
                      <button
                        className="border border-[#E6E6E6] bg-white text-[#1E1E1E] px-6 py-2 rounded-md font-medium transition-colors shadow-sm"
                        onClick={handleCancel}
                        disabled={isSaving}
                      >
                        Cancel
                      </button>
                      <button
                        className="bg-[#2283FF] text-[#F5F5F5] px-6 py-2 rounded-md font-medium transition-colors disabled:opacity-60"
                        onClick={handleSave}
                        disabled={isSaving}
                      >
                        {isSaving ? "Saving..." : "Save changes"}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white py-4 flex items-center">
          <div className="sm:px-14 px-4">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-4 font-medium text-sm rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? "bg-[#F3F4F6] text-[#0B0B0B]"
                      : "text-[#1E1E1ECC] hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-[#F8F9FB] rounded-lg shadow-sm">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default AgencyAdminProfile;
